import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { DataItem } from '../../../../../../../@types/DataItem';
import * as filterServices from '../../../../../../../components/FilterSection/services';
import { useDebounce } from '../../../../../../../hooks';
import { checkIfIsLoading } from '../../../../../../../utils';
import { GETNegociacoesOwners } from '../../NegociacoesSalvas.services';

export type NegociacoesSalvasFilterKey =
  | 'productIds'
  | 'storeIds'
  | 'clusters'
  | 'supplier'
  | 'owner'
  | 'status'
  | 'visionSaved';

export type NegociacoesSalvasFiltersState = Partial<Record<NegociacoesSalvasFilterKey, string[]>>;

export type UseNegociacoesSalvasFiltersProps = {
  data: Partial<Record<NegociacoesSalvasFilterKey, DataItem[]>>;
  value: NegociacoesSalvasFiltersState;
  loading: Partial<Record<NegociacoesSalvasFilterKey, boolean>>;
  onSearch: (key: NegociacoesSalvasFilterKey, query: string) => void;
  onChange: (key: NegociacoesSalvasFilterKey, value: string[]) => void;
  onClean: (key: NegociacoesSalvasFilterKey) => void;
  onCleanAll: () => void;
  onSelectAll: (key: NegociacoesSalvasFilterKey, checked: boolean) => void;
};

const STATUS_OPTIONS: DataItem[] = [
  { label: 'Em negociação', value: 'ONGOING' },
  { label: 'Negócio fechado', value: 'CLOSED' },
];

const VISION_SAVED_OPTIONS: DataItem[] = [
  { label: 'Loja', value: 'STORE' },
  { label: 'Cluster', value: 'CLUSTER' },
  { label: 'Rede', value: 'NETWORK' },
];

export const useNegociacoesSalvasFilters = (): UseNegociacoesSalvasFiltersProps => {
  const debounce = useDebounce();
  const [data, setData] = useState<Partial<Record<NegociacoesSalvasFilterKey, DataItem[]>>>({});
  const [cache, setCache] = useState<Partial<Record<NegociacoesSalvasFilterKey, DataItem[]>>>({});
  const [query, setQuery] = useState<Partial<Record<NegociacoesSalvasFilterKey, string>>>({});
  const [value, setValue] = useState<NegociacoesSalvasFiltersState>({});

  const handleSuccess = useCallback(
    (key: NegociacoesSalvasFilterKey) => {
      return (response: DataItem[]) => {
        setData(oldData => ({
          ...oldData,
          [key]: _.uniqBy([...(cache[key] ?? []), ...(response ?? [])], 'value'),
        }));
      };
    },
    [cache],
  );

  const handleSearch = useCallback(
    (key: NegociacoesSalvasFilterKey, searchQuery: string) => {
      debounce(() => {
        setQuery(oldQuery => ({ ...oldQuery, [key]: searchQuery }));
      });
    },
    [debounce],
  );

  const handleChange = useCallback(
    (key: NegociacoesSalvasFilterKey, nextValue: string[]) => {
      setValue(oldValue => ({ ...oldValue, [key]: nextValue }));
      setCache(oldCache => ({
        ...oldCache,
        [key]: data[key]?.filter(item => nextValue.includes(item.value)) ?? [],
      }));
    },
    [data],
  );

  const handleClean = useCallback((key: NegociacoesSalvasFilterKey) => {
    setQuery(oldQuery => ({ ...oldQuery, [key]: '' }));
    setValue(oldValue => ({ ...oldValue, [key]: [] }));
    setCache(oldCache => ({ ...oldCache, [key]: [] }));
  }, []);

  const handleCleanAll = useCallback(() => {
    setQuery({});
    setValue({});
    setCache({});
  }, []);

  const handleSelectAll = useCallback(
    (key: NegociacoesSalvasFilterKey, checked: boolean) => {
      const nextCache = checked ? data[key] ?? [] : [];

      setValue(oldValue => ({
        ...oldValue,
        [key]: nextCache.map(item => item.value),
      }));
      setCache(oldCache => ({
        ...oldCache,
        [key]: nextCache,
      }));
    },
    [data],
  );

  const { fetchStatus: loadingProducts } = useQuery({
    queryKey: ['negociacoes-salvas.filters.productIds', query.productIds],
    queryFn: () => filterServices.listProducts(query.productIds),
    onSuccess: handleSuccess('productIds'),
    retry: false,
  });

  const { fetchStatus: loadingStoreIds } = useQuery({
    queryKey: ['negociacoes-salvas.filters.storeIds', query.storeIds],
    queryFn: () => filterServices.listStores(query.storeIds),
    onSuccess: handleSuccess('storeIds'),
    retry: false,
  });

  const { fetchStatus: loadingClusters } = useQuery({
    queryKey: ['negociacoes-salvas.filters.clusters', query.clusters],
    queryFn: () => filterServices.listClusters(query.clusters),
    onSuccess: handleSuccess('clusters'),
    retry: false,
  });

  const { fetchStatus: loadingSuppliers } = useQuery({
    queryKey: ['negociacoes-salvas.filters.supplier', query.supplier],
    queryFn: () => filterServices.listSuppliers(query.supplier),
    onSuccess: handleSuccess('supplier'),
    retry: false,
  });

  const { fetchStatus: loadingOwners } = useQuery({
    queryKey: ['negociacoes-salvas.filters.owner', query.owner],
    queryFn: () => GETNegociacoesOwners(query.owner),
    onSuccess: handleSuccess('owner'),
    retry: false,
  });

  const { fetchStatus: loadingStatus } = useQuery({
    queryKey: ['negociacoes-salvas.filters.status'],
    queryFn: async () => STATUS_OPTIONS,
    onSuccess: handleSuccess('status'),
    retry: false,
  });

  const { fetchStatus: loadingVisionSaved } = useQuery({
    queryKey: ['negociacoes-salvas.filters.visionSaved'],
    queryFn: async () => VISION_SAVED_OPTIONS,
    onSuccess: handleSuccess('visionSaved'),
    retry: false,
  });

  const loading = useMemo(
    () => ({
      productIds: checkIfIsLoading(loadingProducts),
      storeIds: checkIfIsLoading(loadingStoreIds),
      clusters: checkIfIsLoading(loadingClusters),
      supplier: checkIfIsLoading(loadingSuppliers),
      owner: checkIfIsLoading(loadingOwners),
      status: checkIfIsLoading(loadingStatus),
      visionSaved: checkIfIsLoading(loadingVisionSaved),
    }),
    [
      loadingClusters,
      loadingOwners,
      loadingProducts,
      loadingStatus,
      loadingStoreIds,
      loadingSuppliers,
      loadingVisionSaved,
    ],
  );

  return {
    data,
    value,
    loading,
    onSearch: handleSearch,
    onChange: handleChange,
    onClean: handleClean,
    onCleanAll: handleCleanAll,
    onSelectAll: handleSelectAll,
  };
};
